/**
 * The simulation's recurring people. Every feature uses these same records, so
 * Ate Joy has the same face, badge and city on the wall, in Stories and in the
 * PDF. All of them are fictional. Clay avatars are reused from the landing set;
 * people without one render as a gradient initial (PersonAvatar).
 */
import avatarAunt from "../../assets/landing/avatar-aunt.webp";
import avatarCoach from "../../assets/landing/avatar-coach.webp";
import avatarGrandparents from "../../assets/landing/avatar-grandparents.webp";
import avatarNurse from "../../assets/landing/avatar-nurse.webp";
import avatarSitter from "../../assets/landing/avatar-sitter.webp";
import avatarTeacher from "../../assets/landing/avatar-teacher.webp";
import type { AccentName } from "../../lib/accents";

export type BadgeId =
  | "mentorParent"
  | "verifiedNurse"
  | "verifiedDoctor"
  | "verifiedTeacher"
  | "foundingMember"
  | "storySharer"
  | "kindNeighbor"
  | "preparedParent";

export interface BadgeInfo {
  label: string;
  accent: AccentName;
  /** Credential-checked (shows the "How we verify" explanation). */
  verified?: boolean;
}

export const BADGES: Record<BadgeId, BadgeInfo> = {
  mentorParent: { label: "Mentor Parent", accent: "amber" },
  verifiedNurse: { label: "Verified Nurse", accent: "emerald", verified: true },
  verifiedDoctor: { label: "Verified Doctor", accent: "emerald", verified: true },
  verifiedTeacher: { label: "Verified Teacher", accent: "indigo", verified: true },
  foundingMember: { label: "Founding Member", accent: "violet" },
  storySharer: { label: "Story Sharer", accent: "rose" },
  kindNeighbor: { label: "Kind Neighbor", accent: "sky" },
  preparedParent: { label: "Prepared Parent", accent: "amber" },
};

export interface SimPerson {
  id: string;
  name: string;
  /** How the community refers to them ("Ate Joy", "Nurse Rhea"). */
  shortName: string;
  role: string;
  city: string;
  /** Clay avatar URL, or null for a gradient initial. */
  avatar: string | null;
  badges: readonly BadgeId[];
}

export const PEOPLE = {
  liza: {
    id: "liza",
    name: "Liza Santos",
    shortName: "Liza",
    role: "First-time mom · baby Ali, 2 months",
    city: "Quezon City",
    avatar: avatarSitter,
    badges: ["foundingMember"],
  },
  ateJoy: {
    id: "ate-joy",
    name: "Joy Mercado",
    shortName: "Ate Joy",
    role: "Mom of three",
    city: "Marikina",
    avatar: avatarAunt,
    badges: ["mentorParent", "foundingMember"],
  },
  nurseRhea: {
    id: "nurse-rhea",
    name: "Rhea Villanueva, RN",
    shortName: "Nurse Rhea",
    role: "Pediatric nurse · Sta. Clara Pediatric Clinic",
    city: "Quezon City",
    avatar: avatarNurse,
    badges: ["verifiedNurse"],
  },
  teacherPaolo: {
    id: "teacher-paolo",
    name: "Paolo Reyes",
    shortName: "Teacher Paolo",
    role: "Admin · Little Bayani Learning Center",
    city: "Tacloban City",
    avatar: avatarTeacher,
    badges: ["verifiedTeacher"],
  },
  lolaNena: {
    id: "lola-nena",
    name: "Nena & Ben Cruz",
    shortName: "Lola Nena",
    role: "Grandparents of four",
    city: "Iloilo City",
    avatar: avatarGrandparents,
    badges: [],
  },
  mia: {
    id: "mia",
    name: "Mia Dela Cruz",
    shortName: "Mia",
    role: "Toddler mom",
    city: "Cebu City",
    avatar: avatarCoach,
    badges: ["foundingMember"],
  },
  marco: {
    id: "marco",
    name: "Marco Villareal",
    shortName: "Marco",
    role: "Dad of a newborn",
    city: "Pasig",
    avatar: null,
    badges: ["foundingMember"],
  },
  carla: {
    id: "carla",
    name: "Carla Domingo",
    shortName: "Carla",
    role: "Marketing · Bayanihan Mart",
    city: "Makati",
    avatar: null,
    badges: [],
  },
  team: {
    id: "team",
    name: "The Leena's List team",
    shortName: "Leena's List",
    role: "Founders",
    city: "Philippines",
    avatar: null,
    badges: [],
  },
} as const satisfies Record<string, SimPerson>;

export type PersonKey = keyof typeof PEOPLE;
